import {Component, OnInit, Inject, OnChanges, SimpleChanges, DestroyRef, inject, EventEmitter} from '@angular/core';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {defer, of, throwError} from 'rxjs';
import {catchError, debounceTime, filter, map, switchMap, tap} from 'rxjs/operators';
import { TreeNode } from '@ali-hm/angular-tree-component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MenuKeyContext} from '@emm/core/models/menu-key.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {ItemService} from '@emm/core/services/item.service';
import {MenuService} from '@emm/core/services/menu.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';
import {wrap} from '@emm/core/util/rx.util';
import {API_ERRORS} from "@emm/core/util/api-constants";

@Component({
    selector: 'emm-delete-child-item-dialog',
    styleUrls: ['./delete-child-item-dialog.component.scss'],
    templateUrl: './delete-child-item-dialog.component.html'
})
export class DeleteChildItemDialogComponent implements OnInit, OnChanges {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    childCount: number;
    childDeletePath;
    color = '#e5173f';
    deleteItemToggle = false;
    diameter = 25;
    featureScope: string;
    firstFormGroup: FormGroup;
    input: FormControl = new FormControl();
    isLinear = true;
    menuKey: MenuKeyContext;
    mode = 'indeterminate';
    options: LocationSet[];
    parent: TreeNode;
    parentCount: number;
    parentDeletePath;
    searched: boolean;
    secondFormGroup: FormGroup;
    selectedChild;
    spinner = false;
    subMenuOnly: FormControl = new FormControl();
    submenus;
    currentSubmenu;
    selectedSubmenus: string[] = [];

    filterValue = '';
    filterSize: number;

    subMenu;
    topChips = [];
    bottomChips = [];
    selectedBottomChips = [];
    topChipsChanged$ = new EventEmitter();
    bottomChipsChanged$ = new EventEmitter();
    pathChanged$ = new EventEmitter();
    path;
    showPath = false;
    matchCount: number;


    constructor(
        public dialogRef: MatDialogRef<DeleteChildItemDialogComponent>,
        private formBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private itemService: ItemService,
        private menuService: MenuService,
        private notificationService: NotificationService,
        private treeFacade: MenuTreeFacadeService,
        private api: MenuApiService,
    ) {}



    ngOnInit(): void {
        this.featureScope = this.data.featureScope;
        this.parent = this.data.parent;
        this.menuKey = this.data.menuKey;
        this.treeFacade.resetCascadeSubmenus();


        this.input.valueChanges
            .pipe(
                filter((value: any) => typeof value === 'string'),
                switchMap((value) => {
                    if (!value || value.length < 2) {
                        this.options = [];
                        return of(null);
                    }
                    return this.itemService
                        .filterItems(value, null, 25)
                        .pipe(
                            tap((results) => {
                                const exactMatch = results.filter((i) => i.tag === value);
                                if (exactMatch.length > 0) {
                                    this.options = exactMatch;
                                } else {
                                    this.options = results;
                                }
                            }),
                            takeUntilDestroyed(this.destroyRef)
                        );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();



        this.firstFormGroup = this.formBuilder.group({
            subMenuOnly: new FormControl('')
        });
        this.secondFormGroup = this.formBuilder.group({
            input: new FormControl('')
        });

        let allPath;
        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            allPath = this.menuService.getParentCascadePathForAll(this.parent.data.data.tagPath, this.featureScope);
        } else {
            allPath = this.menuService.getParentCascadePathForAll(this.parent.data.data.tag, this.featureScope);
        }

        this.findParents(this.featureScope, allPath)
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
        this.subMenuOnly.valueChanges
            .pipe(
                tap(() => {
                    this.input.setValue(null);
                    this.searched = true;
                    this.selectedChild = null;
                    this.childCount = 0;
                }),
                map((value: any) => {
                    let path: string = allPath;
                    if (value === true) {
                        const subMenu = this.findSubMenuParent(this.parent, null);
                        path = this.menuService.getParentCascadePathForSubmenu(
                            subMenu.data.data.jsonPath,
                            this.parent.data.data.tag,
                            this.featureScope
                        );
                    }
                    return path;
                }),
                switchMap((path) => this.findParents(this.featureScope, path)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        // simulate radio button where only 1 chip can be selected
        this.topChipsChanged$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((chip) => {
                const newValue = !chip.selected;
                chip.selected = newValue;
                this.topChips.filter((ch) => chip.tag !== ch.tag).forEach((ch) => (ch.selected = !newValue));
                this.subMenu = this.topChips.filter((ch) => ch.tag !== 'ROOT').filter((ch) => ch.selected)[0];
                this.pathChanged$.emit();
            });

        this.bottomChipsChanged$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((chip) => {
                // validate deselecting bottom chip (item)
                if (chip.tag === this.data.parent.data.data.tag) {
                    this.notificationService.error('Cannot deselect item to be updated');
                    return;
                }

                // toggle selected chip
                const newValue = !chip.selected;

                // toggle descendant chips
                chip.selected = newValue;
                if (newValue) {
                    // set all child descendants to true
                    const match = this.bottomChips.indexOf(chip);
                    this.bottomChips.filter((ch, index) => index > match).forEach((ch) => (ch.selected = true));
                }

                // Check if the deselection creates 2 or more gaps
                const selectedIndexes = this.bottomChips
                    .map((ch, index) => (ch.selected ? index : -1))
                    .filter(index => index !== -1);

                const sortedIndexes = selectedIndexes.sort((a, b) => a - b);
                const gapCount = sortedIndexes.reduce((count, index, i) => {
                    if (i < sortedIndexes.length - 1 && sortedIndexes[i + 1] - index > 1) {
                        return count + 1;
                    }
                    return count;
                }, 0);

                if (gapCount > 1) {
                    // Revert the deselection
                    chip.selected = !chip.selected;
                    this.notificationService.error('Cannot create 2 or more gaps in selected chips');
                }

                this.pathChanged$.emit();
            });

        // update json path
        this.pathChanged$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.generatePath();
            });

        this.setSubmenus();
        this.initChips();
        this.generatePath();
    }


    ngOnChanges(changes: SimpleChanges) {
        this.refreshCascadeSubmenus();
    }

    private findSubMenuParent(parent: TreeNode, previousParent: TreeNode): TreeNode {
        const nextParent = parent.parent;

        // submenu is the last previous parent before reaching root which has no more parent
        if (nextParent === null) {
            return previousParent;
        }

        return this.findSubMenuParent(nextParent, parent);
    }


    refreshCascadeSubmenus() {
        this.treeFacade.selectedSubmenusDelete$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
            for (const submenu of result) {
                if (this.selectedSubmenus.includes(submenu)) {
                    this.selectedSubmenus.filter(element => element !== submenu);
                } else {
                    this.selectedSubmenus.push(submenu);
                }
            }});
    }


    deselectCascadeSubmenus(submenuTag: string) {
        if (this.selectedSubmenus.includes(submenuTag)) {
            this.selectedSubmenus.splice(this.selectedSubmenus.indexOf((submenuTag)));
        }
    }


    displayFn(option): string | undefined {
        return option ? option.name : undefined;
    }



    onSave(): void {
        const result = {
            item: this.parent.data.data.value,
            childPath: this.parentDeletePath
        };
        of(this.treeFacade.deleteItemCascade(result.childPath, result.item))
            .pipe(
                tap((_) => {
                    this.notificationService.success('Deleted Successfully');
                }),
                tap(() => this.dialogRef.close(result)),
                switchMap(() => this.treeFacade.reloadItems()),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe({
            error: (error) => {
                this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
            }
        });
    }



    onNoClick(): void {
        this.dialogRef.close();
    }



    onChange(): void {
        this.selectedChild = null;
    }


    onSelect(e: MouseEvent): void {
        this.searched = true;
        this.spinner = true;
        this.deleteItemToggle = false;
        this.selectedChild = this.parent.data.data;
        const path = this.menuService.getDeleteCascadePath(this.parentDeletePath, this.selectedChild.tag, this.featureScope);
        this.refreshCascadeSubmenus();
        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            this.menuService
                .countMatchesDelete(this.featureScope, this.data.effectiveDate, this.menuKey, path,
                    this.selectedSubmenus.length === 0 ? [this.currentSubmenu] : this.selectedSubmenus)
                .pipe(
                    catchError(() => {
                        const errorMessage = 'Error searching child nodes for parent';
                        this.notificationService.error(errorMessage);
                        return throwError(errorMessage);
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((results) => {
                    this.spinner = false;
                    this.childCount = results;
                    if (results > 0) {
                        this.deleteItemToggle = true;
                        this.childDeletePath = path;
                    }
                });
        }
    }



    findParents(featureScope, path) {
        const before = () => {
            this.parentDeletePath = path;
            this.spinner = true;
            const pathArray = path.split('/');
            this.currentSubmenu = pathArray[1];
        };
        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            const source$ = this.menuService
                .countMatchesDelete(featureScope, this.data.effectiveDate, this.menuKey, path, this.selectedSubmenus)
                .pipe(
                    tap((results) => {
                        this.searched = true;
                        this.spinner = false;
                        this.parentCount = results;
                        this.matchCount = results;
                    }),
                    catchError(() => {
                        const errorMessage = 'Error searching parent nodes';
                        this.notificationService.error(errorMessage);
                        return throwError(errorMessage);
                    })
                );

            return wrap({
                before,
                source$
            });
        }
    }

    updateAllSelected(checked, checkedValue) {
        if (!(this.selectedSubmenus.includes(this.currentSubmenu))) {
            this.treeFacade.selectCascadeSubmenus(this.currentSubmenu);
        }

        this.spinner = true;
        if (checked) {
            this.treeFacade.selectCascadeSubmenus(checkedValue);
            this.refreshCascadeSubmenus();
        } else {
            this.treeFacade.deselectCascadeSubmenus(checkedValue);
            this.deselectCascadeSubmenus(checkedValue);
        }

        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            this.menuService.countMatchesDelete(this.featureScope, this.data.effectiveDate, this.menuKey,
                this.parentDeletePath, this.selectedSubmenus.length === 0 ? [this.currentSubmenu] : this.selectedSubmenus)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(results => {
                this.parentCount = results;
                this.matchCount = results;
                this.spinner = false;
                catchError(() => {
                    const errorMessage = 'Error searching parent nodes';
                    this.notificationService.error(errorMessage);
                    return throwError(errorMessage);
                });
            });
        }
    }

    onFilterChange($event: Event) {
        const filterStr = ($event.target as HTMLInputElement).value;
        this.filterSubmenus(filterStr.toLowerCase());
    }

    private filterSubmenus(value: string): void {
        this.setSubmenus(value?.toLowerCase());
    }

    public clearFilter() {
        this.filterValue = '';
        this.setSubmenus();
    }

    private setSubmenus(filterStr?:string) {
        this.api.getSubmenusByFeatureScope(this.featureScope, this.menuKey)
            .pipe(
                debounceTime(300),
                map((result) => {
                    if (filterStr) {
                        return result.filter((item) => item.name.includes(filterStr)
                            || item.id.toLowerCase().includes(filterStr));
                    } else {
                        return result;
                    }
                }),
                map((submenus) => this.submenus = submenus),
                tap((submenus) => this.filterSize = submenus.length),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    initChips(): void {
        // find item hierarchy
        const nodes = [];
        nodes.push(this.createChip(this.parent?.data?.data, true));
        this.findChipParents(this.parent).forEach((parent) =>
            nodes.push(this.createChip(parent?.data?.data, false))
        );
        const chips = nodes.reverse();

        // split item path to top-level (root / submenu) and bottom-level (parent ancestors)
        const rootChipName = 'Submenu';
        this.topChips.push({
            name: rootChipName,
            selected: true,
            tag: 'ROOT'
        });
        this.bottomChips = chips.slice(1);
    }

    createChip({name = '', tag = ''} = {}, selected: boolean): {} {
        return {name, selected, tag};
    }

    findChipParents(item: TreeNode): TreeNode[] {
        return this.findChipParent(item, []);
    }

    findChipParent(node: TreeNode, parents: TreeNode[]): TreeNode[] {
        const parent = node?.parent;
        if (parent && !node.isRoot) {
            parents.push(parent);
            return this.findChipParent(parent, parents);
        }
        return parents;
    }

    generatePath(): void {
        this.path = null;
        const selectedBottomChips = this.bottomChips.filter((chip => chip.selected));
        this.selectedBottomChips = selectedBottomChips;
        this.path = this.getPath(selectedBottomChips);
        if (this.featureScope && this.path && this.path.ancestorMatchPath && this.path.itemMatchPath){
            this.findMatches(this.featureScope, this.path)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe();
        }
    }

    getPath(selectedBottomChips): object | string {
        let fullTagPath = '';
        let ancestorMatchPath = '';
        let itemMatchPath = '';

        // more than 2 selected groups of chips is not allowed,
        if (selectedBottomChips.length === this.bottomChips.length) {
            for (const chip of selectedBottomChips) {
                fullTagPath += `/${chip.tag}`;
            }
            return {itemMatchPath: fullTagPath};
        } else {
            for (const chip of selectedBottomChips) {
                // if consecutive, add to ancestor match path
                if (selectedBottomChips.indexOf(chip) === this.bottomChips.indexOf(chip)) {
                    ancestorMatchPath += `/${chip.tag}`;
                }
                // if not consecutive, add to item match path
                else {
                    if (selectedBottomChips.length > 1) {
                        ancestorMatchPath.includes(selectedBottomChips[0].tag) ? ancestorMatchPath += `` :
                            ancestorMatchPath += `/${selectedBottomChips[0].tag}`;

                        for (let i = 1; i < selectedBottomChips.length; i++) {
                            itemMatchPath.includes(selectedBottomChips[i].tag) ||
                            ancestorMatchPath.includes(selectedBottomChips[i].tag) ? itemMatchPath += `` :
                                itemMatchPath += `/${selectedBottomChips[i].tag}`;
                        }
                    } else {
                        itemMatchPath += `/${chip.tag}`;
                    }
                }
            }
            return {
                ancestorMatchPath,
                itemMatchPath
            };
        }
    }

    findMatches(featureScope, path): any { // todo: type
        const before = () => {
            this.spinner = true;
        };
        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            const source$ = this.menuService
                .countMatchesForUpdateCascade(featureScope, this.data.effectiveDate, this.menuKey, path,
                    this.selectedSubmenus.length === 0 ? [this.currentSubmenu] : this.selectedSubmenus)
                .pipe(
                    tap((results) => {
                        this.spinner = false;
                        this.matchCount = results;
                    }),
                    catchError(() => {
                        const errorMessage = 'Error searching matches';
                        this.notificationService.error(errorMessage);
                        return throwError(errorMessage);
                    })
                );

            return wrap({
                before,
                source$
            });
        }
    }

    toggleSelectBottomChip(chip): void {
        this.bottomChipsChanged$.emit(chip);
    }

    togglePath(): void {
        this.showPath = !this.showPath;
    }



}
